import {
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
  PrimaryButton,
} from "@fluentui/react";
import { toast } from "components/FluentToast";
import TextInputField from "components/controls/TextInputField";
import { Formik } from "formik";
import { useDuplicateFleet } from "hooks/data/mutations/useDuplicateFleet";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import { delay } from "library/delay";
import React from "react";
import { useTranslation } from "react-i18next";
import { Fleet } from "types/types";
import { setYupLocale } from "utils/setYupLocale";
import * as yup from "yup";
import { FormSpinner } from "../../components/FormSpinner";
import FleetClientComboBox from "./FleetClientComboBox";

type Props = {
  isOpen: boolean;
  fleet: Fleet | null;
  onDismiss: () => void;
  onSuccess?: (duplicatedFleet?: any) => void;
};

export const DuplicateFleetDialog = ({
  isOpen,
  fleet,
  onDismiss,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const phraseActivated = usePhraseActivationStatus().activated;

  const { mutate: onDuplicateFleet, isLoading } = useDuplicateFleet();

  const onSubmit = (values) => {
    onDuplicateFleet(
      {
        fleetId: fleet?.fleetId,
        body: {
          fleetName: values.fleetName?.trim(),
          webBasePartnerNumber: values.webBasePartnerNumber,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(t("bfm.success"));
          onDismiss();
          onSuccess?.(res?.data);
        },
      }
    );
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={isLoading ? () => {} : onDismiss}
      minWidth={520}
      maxWidth={520}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
        styles: {
          main: { overflow: "visible" },
          scrollableContent: { overflow: "visible" },
        },
      }}
      dialogContentProps={{
        type: DialogType.normal,
        styles: {
          content: { overflow: "visible" },
          inner: { overflow: "visible" },
          innerContent: { overflow: "visible" },
        },
        title: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon
              iconName="Copy"
              style={{
                fontSize: "25px",
                marginRight: "10px",
              }}
            />
            {t("bfm.duplicateFleet.label")}
          </div>
        ),
      }}
    >
      <Formik
        initialValues={{ fleetName: "", webBasePartnerNumber: null }}
        enableReinitialize
        validationSchema={() => {
          setYupLocale(t);
          return yup.object().shape({
            fleetName: yup.string().trim().required().max(128),
            webBasePartnerNumber: yup.number().nullable().required(),
          });
        }}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting }) => {
          onSubmit(values);
          await delay(400);
          setSubmitting(false);
        }}
      >
        {(props) => {
          const { values, handleChange, errors, handleSubmit } = props;

          return (
            <form onSubmit={handleSubmit}>
              <TextInputField
                value={values.fleetName}
                onChange={handleChange}
                errors={errors}
                required
                name={"fleetName"}
                disabled={false}
                label={"bfm.fleetForm.fleetName.label"}
              />
              {/* only clients of the duplicated fleet's country can be picked */}
              <FleetClientComboBox
                required
                name={"webBasePartnerNumber"}
                label={"bfm.duplicateFleet.selectClient.label"}
                countryCodeId={fleet?.countryCode}
              />
              <DialogFooter>
                <PrimaryButton
                  text={t("greco.save")}
                  type="submit"
                  iconProps={{
                    iconName: "Copy",
                  }}
                  disabled={isLoading}
                />
              </DialogFooter>
              {isLoading && <FormSpinner />}
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};
